export interface WalkthroughStep {
  title: string;
  detail: string;
}

export interface GenAIScenario {
  id: string;
  title: string;
  blurb: string;
  body: string;
  /** AIBMM level this scenario belongs to */
  level: number;
  /** Short action label for the launch button */
  actionLabel: string;
  /** Lucide icon name for the scenario card */
  icon: string;
  /** Filenames under /public/downloads that should be uploaded for this scenario */
  dataFiles?: { filename: string; label: string; rows: string }[];
  /** Authored step-by-step walkthrough for the training page */
  walkthrough: WalkthroughStep[];
  /** Image-generation prompt for producing a printable poster from this scenario */
  posterPrompt?: string;
}

// ── Scenario 1: Original AAR ─────────────────────────────────────────────────

const AAR_NORTHERN_HORIZON_PROMPT = `You are reviewing the following notional After-Action Report for an AIBMM Level 3 training exercise. All names, units, dates, and data are fictional. Please:
1) Identify OPSEC, conduct, and process issues using BLUF format.
2) Apply 8-Step Problem Solving / Fishbone framing to the root-cause observations.
3) Recommend corrective actions and a CCIR/SIR routing summary.

--- BEGIN NOTIONAL DOCUMENT ---

UNCLASSIFIED // FOR TRAINING USE ONLY

NOTIONAL DOCUMENT — ALL NAMES, UNITS, DATES, AND DATA ARE FICTIONAL — NOT A REAL AAR

DEPARTMENT OF THE AIR FORCE

999TH AIR REFUELING WING (AFRC) — NOTIONAL

Notional Forward Operating Site CARBON, Saskatchewania

14 March 2026

MEMORANDUM FOR:  999 ARW/CC

FROM:  999 OG/CC

SUBJECT:  After-Action Report (AAR) — Operation NORTHERN HORIZON 26-04, 02–09 March 2026, in Support of Joint Exercise BREAKWATER FROST

1.  PURPOSE. This AAR documents observations and findings from Operation NORTHERN HORIZON 26-04 (02–09 March 2026), conducted from Notional Forward Operating Site CARBON, Republic of Saskatchewania, in support of Joint Exercise BREAKWATER FROST. Submitted IAW notional AFI 90-1601, paragraph 4.3.

2.  KEY PERSONNEL.

a.  Mission Commander: Lt Col John A. Smith, 999 OG/CC, john.smith.1@us.af.mil.notional, (555) 000-0001, DSN 555-0001, residence 100 Notional Way, Anytown, USA 00000.

b.  Operations Lead: Maj Jane B. Doe, 9991 ARS/DO, jane.doe.2@us.af.mil.notional, (555) 000-0002.

c.  Senior Enlisted Leader: CMSgt Alex T. Roe, 9991 ARS SEL, SSN 000-00-0000, DOB 01 JAN 1980, EDIPI 0000000000, alex.roe.3@us.af.mil.notional, mobile (555) 000-0003, spouse [Spouse Name redacted].

d.  Maintenance Lead: CMSgt Pat M. Lee, 999 MXG, pat.lee.4@us.af.mil.notional, (555) 000-0004.

e.  Deployed footprint: 87 personnel from 999 ARW; 23 personnel from notional 9992 OSS; 14 personnel from notional 9999 FW (receiver flight liaison).

3.  ASSETS.

a.  Six (6) KC-46A Pegasus, tail numbers 16-46032, 16-46044, 17-46058, 17-46071, 18-46089, 19-46104.

b.  Two (2) KC-135R Stratotanker, tail numbers 63-7995, 64-14829.

c.  Approved exercise budget: $4,800,000. Final expenditure: $6,047,310. Variance: +$1,247,310 (+26.0%) — variance justification due to 999 ARW/A8 NLT 28 March 2026.

4.  KEY EVENTS AND FINDINGS.

a.  PRE-DEPLOYMENT (28 Feb 2026). Maintenance handoff documentation transferred between 999 MXG and 9992 OSS via personal Gmail accounts to expedite turnover ahead of weather closure. Finding: bypass of authorized data flow IAW notional AFI 33-321, para 2.6.4. Responsible: TSgt John Q. Public (john.public.5@us.af.mil.notional), 9992 OSS Plans. Verbal counseling administered 04 March 2026 by CMSgt Alex T. Roe and documented on AF Form 174-N.

b.  INFLIGHT INCIDENT (04 March 2026, 1437Z). KC-46A tail 17-46058 experienced refueling boom hydraulic failure at FL310 over grid 51°24′N 105°42′W during rendezvous with notional receiver flight DAGGER 21 (four F-15E from notional 9999 FW). Receivers declared JOKER fuel and RTB'd to notional Forward Operating Base STERLING. No injuries. Tanker RTB'd Site CARBON. Estimated repair cost: $87,400. Estimated downtime: 72 hours. Boom hydraulic discrepancy traced to subcomponent P/N NTL-44782-X, serial NTL-SN-019334; depot-level inspection pending.

c.  PERSONNEL / CONTRACTOR ISSUE (06 March 2026). Maj Jane B. Doe reported via secure message to Lt Col John A. Smith an incident of inappropriate communication from contractor representative Mr. James K. Doe, Skyline Defense Logistics LLC (james.doe@skylinedefense.com.notional, (555) 000-0005, residence 200 Notional Ave, Anytown, USA 00000). Reported to notional 999 OSI Det 4 per AFI 71-101. Case # NTL-2026-0314-OSI. Investigation pending.

d.  OPSEC OBSERVATION (07 March 2026). During off-duty period at notional Solomon Aviator Inn (1247 Aerodrome Way, North Battleford-Notional, SK), three personnel posted geo-tagged Instagram photographs showing aircraft tail numbers, unit patches, and aircrew faces. Personnel: SSgt Mary A. Roe (9991 ARS, mary.roe.7@us.af.mil.notional), A1C Sam B. Doe (999 MXG, sam.doe.2@us.af.mil.notional), SrA Chris C. Lee (9992 OSS, chris.lee@us.af.mil.notional). Counseling administered; posts removed within 4 hours; reviewed by 999 ARW Public Affairs (TSgt Erin Y. Smith, erin.smith.6@us.af.mil.notional).

e.  COMMUNICATIONS GAP (08 March 2026). 9991 ARS ALCC unable to reach 9992 OSS Plans for 47 minutes during weather diversion decision; root cause: notional DMS account access not provisioned for two augmentees pre-deployment. Operational impact: delayed RTB decision for two KC-46A and minor fuel penalty (~3,400 lbs combined).

5.  INITIAL ROOT CAUSE OBSERVATIONS (For Application of 8-Step / Fishbone in Follow-On Review).

a.  Pre-deployment information handoff process is not standardized across participating units; ad-hoc email workarounds emerge when scheduled comms windows close.

b.  OPSEC training does not adequately address personal-device behaviors during OCONUS / notional-international deployments (specifically geo-tag defaults and visible mission indicators).

c.  Contractor conduct expectations not briefed in joint pre-deployment orientation; vendor management gap.

d.  Account-provisioning timeline for augmentees is decoupled from deployment roster finalization; recurring across past three exercises (FROSTHAWK 25-02, BREAKWATER FROST 25-04, NORTHERN HORIZON 26-04).

6.  RECOMMENDATIONS.

a.  Re-emphasize secure data transfer procedures during pre-deployment training and require digital signature on AF Form 4433-N (notional).

b.  Update contractor onboarding to include OPSEC and conduct briefing prior to deployment integration; require background-check refresh on Skyline Defense Logistics personnel for next supporting cycle.

c.  Issue wing-wide OPSEC reminder regarding personal device geo-tagging during deployments; coordinate with 999 ARW PA.

d.  Review KC-46A boom hydraulic maintenance interval with notional Air Force Sustainment Center; recommend reduction from 480 hours to 360 hours pending depot inspection results.

e.  Establish standing 30-day-pre-deployment account-provisioning checkpoint for all augmentees.

f.  Submit budget variance justification to notional 22 AF/A8 NLT 28 March 2026 (routing through 999 ARW/CC).

7.  POINT OF CONTACT. Lt Col John A. Smith, 999 OG/CC, john.smith.1@us.af.mil.notional, (555) 000-0001, DSN 555-0001.

//SIGNED//

JOHN A. SMITH, Lt Col, USAFR
Commander, 999th Operations Group (Notional)

Attachments:
1.  Flight logs, tail 17-46058, 04 March 2026 (NTL)
2.  AF Form 174-N, TSgt John Q. Public counseling (NTL)
3.  Budget variance worksheet (NTL)

Distribution:
999 ARW/CC, 999 ARW/CV, 999 ARW/A3, 999 ARW/A4, 999 ARW/A8, 22 AF/CC (Notional)

NOTIONAL DOCUMENT — ALL NAMES, UNITS, DATES, AND DATA ARE FICTIONAL — NOT A REAL AAR
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL DOCUMENT ---`;

// ── Scenario 2: Depersonalize a Navy FRC record ──────────────────────────────

const DEPERSONALIZE_NAVY_FRC_PROMPT = `NOTIONAL DOCUMENT — ALL NAMES, SERIAL NUMBERS, BuNos, EDIPI, AND UNIT DATA ARE FICTIONAL — TRAINING USE ONLY

I have a notional maintenance record from Fleet Readiness Center Southwest (FRCSW) that contains PII and unit-identifying information. I need to use a general-purpose commercial AI tool (NOT cleared for CUI) to help me draft a maintenance trend analysis memo.

Before I can paste this into a commercial tool, I need your help DEPERSONALIZING it. Please:

1) Replace every instance of PII and CUI-sensitive identifiers with neutral tokens using this scheme:
   - Personnel names → PERSON_1, PERSON_2, etc.
   - EDIPI numbers → EDIPI_1, EDIPI_2, etc.
   - SSNs → SSN_REDACTED
   - Email addresses → EMAIL_1, EMAIL_2, etc.
   - Phone numbers → PHONE_1, PHONE_2, etc.
   - Aircraft BuNos → BUNO_1, BUNO_2, etc.
   - Hull numbers / ship names → HULL_1, HULL_2, etc.
   - Unit designators → UNIT_A, UNIT_B, etc.
   - Specific part serial numbers → PART_SN_1, PART_SN_2, etc.
   - Contractor company names → CONTRACTOR_1, etc.

2) Output the TOKENIZED document (safe to paste into a commercial AI tool).

3) Output a TOKEN MAP TABLE (two columns: Token → Original Value) so I can re-personalize the AI's draft afterward.

--- BEGIN NOTIONAL MAINTENANCE RECORD ---

UNCLASSIFIED // FOR TRAINING USE ONLY
NOTIONAL FLEET READINESS CENTER SOUTHWEST — TRAINING DOCUMENT

DATE: 12 May 2026
PREPARED BY: AME2 Marcus T. Rivera, EDIPI 1234567890, marcus.rivera@navy.mil.notional, (619) 555-0142
REVIEWED BY: LCDR Patricia N. Howell, FRCSW/MO, patricia.howell@navy.mil.notional, (619) 555-0199
DIVISION: Strike Fighter Intermediate Maintenance Department (SFIMD)

SUBJECT: Corrosion Trend Summary — FA-18E/F Engine Bay Frames, CVN-77 (USS George H.W. Bush) Aircraft, May 2026

AIRCRAFT AFFECTED:
1. BuNo 166788 — callsign CAMELOT 401 — assigned VFA-87 (Golden Warriors), CVN-77
2. BuNo 166823 — callsign CAMELOT 404 — assigned VFA-87, CVN-77
3. BuNo 167034 — callsign STRIKE 211 — assigned VFA-31 (Tomcatters), CVN-77

HULL: CVN-77, USS George H.W. Bush, currently at Norfolk Naval Station Pier 12, arriving FRCSW NAS North Island 18 May 2026 for Docking Selected Restricted Availability (DSRA).

FINDINGS:
- All three airframes exhibit grade-3 corrosion on Fwd Engine Bay Frames, Sta 390–418 (P/N NTL-F18-3901-A, S/N NTL-SN-88201, NTL-SN-88209, NTL-SN-88217).
- Corrosion traced to inadequate preservation during at-sea periods; maintenance log entries signed by AD3 Kevin L. Park (EDIPI 9876543210, kevin.park@navy.mil.notional) indicate preservation steps skipped on 14 Feb and 03 Mar 2026 due to flight-deck tempo.
- Contractor inspection (TechTeam Aerospace LLC, contract # N00019-24-C-0551-NTL, POC: James W. Delgado, james.delgado@techteam.com.notional, (858) 555-0310) confirmed findings on 10 May 2026.

RECOMMENDED ACTION:
- Depot-level corrosion repair, estimated 45 days per airframe, $127,400 per airframe.
- Notify VFA-87 Maintenance Officer (LT Samantha K. Okafor, samantha.okafor@navy.mil.notional) and VFA-31 Maintenance Officer (LT Brian C. Moss, brian.moss@navy.mil.notional) NLT 14 May 2026.
- Update NALCOMIS records under work order NTL-WO-2026-0512.

POINT OF CONTACT: AME2 Marcus T. Rivera, (619) 555-0142.

NOTIONAL DOCUMENT — ALL DATA FICTIONAL — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL MAINTENANCE RECORD ---

Please produce the tokenized version and the token map table now.`;

// ── Scenario 3: Re-personalize an AI draft ───────────────────────────────────

const REPERSONALIZE_DRAFT_PROMPT = `NOTIONAL DOCUMENT — ALL NAMES, SERIAL NUMBERS, BuNos, AND UNIT DATA ARE FICTIONAL — TRAINING USE ONLY

I previously depersonalized a maintenance record (removed PII/CUI) and pasted it into a commercial AI tool to draft a maintenance trend analysis memo. The commercial AI produced the draft below, using placeholder tokens. I now need to RE-PERSONALIZE the draft by substituting all tokens back to their real values using the token map I retained.

Please:
1) Replace every token in the DRAFT with its corresponding value from the TOKEN MAP below.
2) Output the final, fully re-personalized memo — ready to route on official letterhead in our IL5 system (GenAI.mil / NIPR).
3) Flag any place where you are uncertain about a substitution.

--- TOKEN MAP ---

PERSON_1 = AME2 Marcus T. Rivera
PERSON_2 = LCDR Patricia N. Howell
PERSON_3 = AD3 Kevin L. Park
PERSON_4 = LT Samantha K. Okafor
PERSON_5 = LT Brian C. Moss
PERSON_6 = James W. Delgado
EDIPI_1 = 1234567890
EDIPI_2 = 9876543210
EMAIL_1 = marcus.rivera@navy.mil.notional
EMAIL_2 = patricia.howell@navy.mil.notional
EMAIL_3 = kevin.park@navy.mil.notional
EMAIL_4 = samantha.okafor@navy.mil.notional
EMAIL_5 = brian.moss@navy.mil.notional
EMAIL_6 = james.delgado@techteam.com.notional
PHONE_1 = (619) 555-0142
PHONE_2 = (619) 555-0199
PHONE_3 = (858) 555-0310
BUNO_1 = 166788
BUNO_2 = 166823
BUNO_3 = 167034
HULL_1 = CVN-77, USS George H.W. Bush
UNIT_A = VFA-87 (Golden Warriors)
UNIT_B = VFA-31 (Tomcatters)
UNIT_C = Fleet Readiness Center Southwest (FRCSW)
CONTRACTOR_1 = TechTeam Aerospace LLC
PART_SN_1 = NTL-SN-88201
PART_SN_2 = NTL-SN-88209
PART_SN_3 = NTL-SN-88217

--- AI-GENERATED DRAFT (with tokens) ---

MEMORANDUM

DATE: 12 May 2026
FROM: PERSON_1, Strike Fighter Intermediate Maintenance Department, UNIT_C
TO: Maintenance Officers, UNIT_A and UNIT_B; UNIT_C/MO
SUBJECT: Corrosion Trend — FA-18E/F Engine Bay Frames, HULL_1 Aircraft

BLUF: Three FA-18E/F airframes (BUNO_1, BUNO_2, BUNO_3) assigned to HULL_1 exhibit grade-3 corrosion on Fwd Engine Bay Frames, Sta 390–418. Depot-level repair required; estimated 45 days / $127,400 per airframe. Action required from UNIT_A and UNIT_B maintenance officers NLT 14 May 2026.

1. BACKGROUND. UNIT_C Strike Fighter Intermediate Maintenance Department identified a corrosion trend across three HULL_1 airframes during pre-DSRA inspection. CONTRACTOR_1 confirmed findings on 10 May 2026.

2. FINDINGS.
   a. BUNO_1 (UNIT_A): Grade-3 corrosion, Sta 390–418, P/N NTL-F18-3901-A (S/N PART_SN_1).
   b. BUNO_2 (UNIT_A): Grade-3 corrosion, same frame location (S/N PART_SN_2).
   c. BUNO_3 (UNIT_B): Grade-3 corrosion, same frame location (S/N PART_SN_3).
   Root cause: preservation steps skipped on 14 Feb and 03 Mar 2026 per maintenance log entries (PERSON_3, EDIPI_2).

3. RECOMMENDED ACTIONS.
   a. Initiate depot-level corrosion repair for all three airframes upon HULL_1 arrival at NAS North Island (est. 18 May 2026).
   b. PERSON_4 (UNIT_A) and PERSON_5 (UNIT_B) acknowledge and concur NLT 14 May 2026.
   c. Update NALCOMIS under work order NTL-WO-2026-0512.

4. POC: PERSON_1, EMAIL_1, PHONE_1.

//SIGNED//
PERSON_2
UNIT_C / Maintenance Officer

NOTIONAL DOCUMENT — ALL DATA FICTIONAL — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END AI-GENERATED DRAFT ---

Please produce the final re-personalized memo now.`;

// ── Scenario 4: Command picnic planner (non-sensitive, public) ───────────────

const COMMAND_PICNIC_PROMPT = `This is a fully non-sensitive, non-CUI planning task — no military operational data involved. I'm using this to demonstrate that a public tool like Claude.ai, ChatGPT, or Gemini is completely appropriate for tasks like this (no need to route through GenAI.mil or depersonalize anything).

Please help me plan a unit command picnic for approximately 120 personnel (Sailors, Soldiers, or Airmen and their families) at a local park pavilion. Draft the following:

1. A friendly all-hands invitation email (keep it upbeat, under 200 words, suitable for posting on a command bulletin board or group chat).

2. An event planning checklist with categories: Food & Drinks, Activities & Games, Logistics & Setup, Communications, Safety & Medical.

3. Three low-cost activity ideas that work well for a mixed group (adults, children, varying fitness levels).

Keep the tone casual and inclusive. The event is on a Saturday in late June, weather expected to be warm and sunny.`;

// ── Scenario 5: Navy carrier availability backlog BLUF ───────────────────────

const NAVY_CARRIER_AVAILABILITY_PROMPT = `NOTIONAL DOCUMENT — ALL SHIP NAMES, DATES, COSTS, AND UNIT DATA ARE FICTIONAL — TRAINING USE ONLY

I am the Deputy Commander of a notional Regional Maintenance Center. I have a table of upcoming carrier maintenance availabilities and need a BLUF-format summary memo for the Admiral's Monday brief. The memo should highlight: (1) total workload and cost exposure, (2) the two highest-risk availabilities (schedule or cost), (3) any workforce / parts concerns, and (4) one recommended action per risk item.

Please draft the BLUF memo (2 pages max) from the data below.

--- BEGIN NOTIONAL DATA TABLE ---

UNCLASSIFIED // FOR TRAINING USE ONLY
NOTIONAL REGIONAL MAINTENANCE CENTER — TRAINING DATA

CARRIER MAINTENANCE AVAILABILITY SCHEDULE — Q3/Q4 FY26 (NOTIONAL)

| Hull        | Ship Name (Notional)         | Availability Type     | Start Date   | Planned End  | Contracted Cost  | Current Status                          | Key Risk                                      |
|-------------|------------------------------|-----------------------|--------------|--------------|------------------|-----------------------------------------|-----------------------------------------------|
| CVN-74-NTL  | USS John C. Stennis (NTL)    | PIA (Planned Incr.)   | 15 Jul 2026  | 14 Jan 2027  | $312M            | 12 days behind; propulsion shaft work   | Propulsion shaft bearing P/N NTL-CVN-5512 on order; 8-week lead time; may slip delivery |
| CVN-76-NTL  | USS Ronald Reagan (NTL)      | DSRA (Docking Sel.)   | 01 Sep 2026  | 28 Feb 2027  | $487M            | Pre-availability; work package 94% complete | Drydock slot conflict with LHA-7-NTL; NAVSEA waiver pending |
| CVN-78-NTL  | USS Gerald R. Ford (NTL)     | ISRA (Incremental)    | 22 Sep 2026  | 15 Dec 2026  | $89M             | On schedule                             | Weapons elevator software patch (v4.2) not yet certified; NLT cert date 15 Aug 2026 |
| CVN-73-NTL  | USS George Washington (NTL)  | CNO Availability      | 10 Oct 2026  | 09 Oct 2028  | $1.8B            | Contract award phase                    | Shipyard capacity: only one eligible yard has drydock availability; sole-source risk |

WORKFORCE NOTE: Journeyman welder shortage at primary shipyard; 38 vacancies as of 01 Jun 2026. Overtime authorizations approved through Sep 2026.

PARTS NOTE: Three long-lead items across CVN-74-NTL and CVN-76-NTL at risk of delaying undocking: P/N NTL-CVN-5512 (8 wk), P/N NTL-CVN-8801 (11 wk), P/N NTL-PP-2204 (6 wk — on expedite).

NOTIONAL DATA — ALL FICTIONAL — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL DATA TABLE ---

Please draft the BLUF memo for the Admiral's brief now.`;

// ── Scenario 6: Air Force Air Logistics Complex — AOG triage memo ────────────

const AF_ALC_AOG_PROMPT = `NOTIONAL DOCUMENT — ALL TAIL NUMBERS, PART NUMBERS, UNIT DESIGNATORS, AND COST DATA ARE FICTIONAL — TRAINING USE ONLY

I am the Deputy Director of Maintenance at a notional Air Logistics Complex (ALC). I have a parts-shortage / Aircraft-On-Ground (AOG) status list from this morning's production stand-up. I need a concise triage memo (1 page) for the Wing Commander that:

1) Lists the AOG aircraft in priority order (mission impact highest first).
2) For each AOG, states: tail number, unit, AOG cause, needed part, estimated resolution date, and recommended action.
3) Flags any item requiring MAJCOM or depot commander decision authority.
4) Includes a "bottom line" paragraph: how many aircraft can be returned to FMC status within 72 hours vs. 7 days vs. >7 days.

Use BLUF format. All data below is notional.

--- BEGIN NOTIONAL AOG STATUS LIST ---

UNCLASSIFIED // FOR TRAINING USE ONLY
NOTIONAL AIR LOGISTICS COMPLEX — PRODUCTION STAND-UP DATA — 09 JUN 2026

| Tail #       | MDS      | Unit (Notional)      | AOG Since   | AOG Cause                                      | Required P/N          | On-Hand Qty | EDD          | Action Taken So Far                        |
|--------------|----------|----------------------|-------------|------------------------------------------------|-----------------------|-------------|--------------|---------------------------------------------|
| NTL-86-0192  | F-15E    | 4 FW / 335 FS (NTL) | 04 Jun 2026 | Radar processor card failure (F110 avionics)   | NTL-APG63-7741        | 0           | 22 Jun 2026  | AWP; manufacturer contacted; EXREP submitted |
| NTL-91-0662  | C-17A    | 437 AW (NTL)         | 07 Jun 2026 | #3 engine compressor blade FOD damage          | NTL-F117-CB-4402      | 0           | 18 Jun 2026  | Cannibal from NTL-93-0118 approved; 48-hr labor |
| NTL-00-1044  | B-2A     | 509 BW (NTL)         | 01 Jun 2026 | Low-observable coating delamination, LH wing   | NTL-LO-COAT-PANEL-17  | 1           | 14 Jun 2026  | Depot team mobilized; COMSEC hold on details; Wing/CC authority required |
| NTL-04-4132  | F-22A    | 1 FW (NTL)           | 08 Jun 2026 | Bleed air duct crack, engine bay               | NTL-F119-BD-0088      | 2           | 11 Jun 2026  | Expedite pull from supply; 24-hr repair estimate |
| NTL-87-0043  | KC-135R  | 92 ARW (NTL)         | 05 Jun 2026 | Landing gear actuator seal failure             | NTL-LG-ACT-7812       | 0           | 27 Jun 2026  | DLA expedite requested; commercial source identified at $4,200/unit |

WORKFORCE: Two-shift operation in effect. Journeyman shortage (avionics): 11 vacancies. Overtime auth through 30 Jun 2026.

NOTIONAL DATA — ALL FICTIONAL — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL AOG STATUS LIST ---

Please draft the triage memo for the Wing Commander now.`;

// ── Scenario 7: Army maintenance depot — work-order prioritization ────────────

const ARMY_DEPOT_WORKORDER_PROMPT = `NOTIONAL DOCUMENT — ALL VEHICLE SERIAL NUMBERS, UNIT DESIGNATORS, COST DATA, AND PERSONNEL ARE FICTIONAL — TRAINING USE ONLY

I am the Production Control Officer at a notional Army depot (modeled on a combined-arms equipment overhaul facility). I have 48 hours of capacity remaining in the current two-week production cycle, and the work-order queue below exceeds available hours by ~30%. I need your help to:

1) Prioritize the work orders using Army maintenance priority criteria: (a) mission criticality / deploying unit, (b) safety of use (SOUR), (c) time-since-deadline, (d) cost efficiency.
2) Identify which work orders should be deferred to the next cycle and explain why.
3) Draft a one-paragraph production summary I can send to the Depot Commander.

Use BLUF format. All data below is notional.

--- BEGIN NOTIONAL WORK-ORDER QUEUE ---

UNCLASSIFIED // FOR TRAINING USE ONLY
NOTIONAL ARMY DEPOT — PRODUCTION CONTROL QUEUE — CYCLE ENDING 20 JUN 2026

Available labor-hours this cycle: 48 hrs (critical path resources)

| WO #         | Equipment          | Unit (Notional)            | Fault Description                              | Est. Hrs | Priority Code | Notes                                                        |
|--------------|--------------------|----------------------------|------------------------------------------------|----------|---------------|--------------------------------------------------------------|
| NTL-WO-4401  | M1A2 SEPv3 (NTL)  | 1-64 AR, 3rd ID (NTL)      | Engine thermostat / cooling system failure     | 12       | URGENT        | Unit deploys NLT 25 Jun 2026; combat-coded                   |
| NTL-WO-4402  | M88A2 HERCULES     | 2-7 IN (NTL)               | Hydraulic cylinder — boom assembly             | 8        | PRIORITY      | Recovery asset; currently non-mission capable                |
| NTL-WO-4403  | HMMWV M1151 (NTL) | 504th MP BN (NTL)          | Brake system failure — SOUR issued             | 4        | URGENT/SOUR   | SOUR tag — vehicle grounded; safety of use restriction active |
| NTL-WO-4404  | M109A7 Paladin     | 1-9 FA, 3rd ID (NTL)       | Traverse gearbox overhaul (scheduled)          | 18       | ROUTINE       | Scheduled overhaul; unit not deploying; 22 days past due     |
| NTL-WO-4405  | M1070 HET (NTL)   | 110th TC BN (NTL)          | Transfer case seal replacement                 | 6        | PRIORITY      | Critical transport asset; one of two HETs in theater support |
| NTL-WO-4406  | M2A3 Bradley       | 3-69 AR (NTL)              | Track tension / road wheel replacement         | 10       | ROUTINE       | Readiness-driven; no deployment date; 8 days past due        |
| NTL-WO-4407  | AN/TPQ-53 Radar    | 1-43 ADA (NTL)             | Power conditioning unit swap                   | 6        | URGENT        | Air defense asset; unit in operational status                |
| NTL-WO-4408  | FMTV M1083 (NTL)  | 127th ASB (NTL)            | Fuel system contamination flush                | 5        | ROUTINE       | Supply support vehicle; unit not time-sensitive               |

TOTAL QUEUE: 69 hrs
AVAILABLE: 48 hrs
SHORTFALL: 21 hrs (must defer ~2–3 work orders)

WORKFORCE NOTE: One master gunner / heavy equipment tech on leave through 15 Jun 2026 (affects M1A2 work).

NOTIONAL DATA — ALL FICTIONAL — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL WORK-ORDER QUEUE ---

Please prioritize the queue and draft the production summary for the Depot Commander now.`;

// ── Scenario 8: BLUF rewrite / policy memo summarizer ───────────────────────

const BLUF_REWRITE_PROMPT = `NOTIONAL DOCUMENT — ALL POLICY DETAILS ARE ILLUSTRATIVE AND FICTIONAL — TRAINING USE ONLY

One of the most practical uses of a military AI assistant is rewriting dense policy memos into actionable BLUF format. I have a notional policy update memo below. Please:

1) Rewrite it as a BLUF (Bottom Line Up Front) summary — 3 paragraphs max:
   - Paragraph 1: The single most important takeaway (one sentence).
   - Paragraph 2: What specifically must change, and by when.
   - Paragraph 3: Who is responsible and where to get more info.

2) Pull out a "key dates / actions" table: columns = Action | Responsible Party | Due Date.

3) Flag any ambiguities in the original policy that a commander would need clarified before publishing down to units.

--- BEGIN NOTIONAL POLICY MEMO ---

UNCLASSIFIED // FOR TRAINING USE ONLY
NOTIONAL DOCUMENT — TRAINING PURPOSES ONLY

DEPARTMENT OF DEFENSE (NOTIONAL)
OFFICE OF THE UNDER SECRETARY OF DEFENSE FOR ACQUISITION AND SUSTAINMENT

MEMORANDUM FOR: Secretaries of the Military Departments; Chiefs of the Military Services; Directors of Defense Agencies

FROM: USD(A&S) (Notional)

SUBJECT: Updated Guidance on Use of Artificial Intelligence Tools for Maintenance Data Analysis at Depot and Intermediate Maintenance Activities — Policy Change Notice 2026-07 (Notional)

DATE: 01 June 2026

1. PURPOSE. This memorandum updates existing guidance on the authorized use of commercial and government-provided AI tools in depot and intermediate maintenance activities, consistent with DoD Directive 3000.09 (Notional), AI Ethics Principles, and the GenAI.mil platform authorization issued 09 December 2025.

2. BACKGROUND. Multiple depot commands have reported inconsistent practices regarding the use of commercial large language models (LLMs) and the DoD-authorized GenAI.mil platform. Some activities have used uncleared commercial AI tools to process maintenance records containing Controlled Unclassified Information (CUI), Personally Identifiable Information (PII), and export-controlled technical data, creating potential compliance, OPSEC, and data-spillage risks.

3. POLICY.

a. Effective 01 July 2026, all maintenance data containing CUI, PII, or export-controlled technical data (EAR/ITAR) must be processed exclusively through DoD-authorized systems operating at the appropriate Impact Level (IL4 or IL5). The GenAI.mil platform (NIPR, IL5) is authorized for CUI handling. Commercial tools (e.g., ChatGPT.com, Claude.ai, Gemini.google.com) are NOT authorized for CUI unless accessed through a DoD-approved enterprise agreement at the required IL.

b. Maintenance personnel who wish to use commercial AI tools for non-CUI administrative tasks (e.g., drafting non-sensitive communications, scheduling, general research) may do so on unclassified government systems, provided no CUI, PII, or controlled technical data is entered.

c. Depot and IMA commanders will, NLT 01 August 2026, publish a unit-level AI Use Policy consistent with this guidance, identifying: (1) authorized tools and their classification ceilings, (2) prohibited use cases, (3) training requirements for personnel using AI tools, and (4) a reporting mechanism for suspected data spillage.

d. Training. All personnel with access to AI tools at depot/IMA activities must complete the DoD AI Literacy Basics course (available on JKO, Course # NTL-AI-101) NLT 30 September 2026.

e. Reporting. Suspected misuse or data spillage involving AI tools will be reported through the existing CUI spillage reporting process (DoDM 5200.01, Vol 3, notional).

4. RESPONSIBILITIES.

a. Service Deputy Chiefs of Staff for Logistics (or equivalent): ensure Service-level implementing instructions published NLT 15 July 2026.

b. Depot/IMA Commanders: publish unit AI Use Policy NLT 01 August 2026; designate an AI Tool Coordinator (officer or GS-12+) NLT 15 July 2026.

c. Training Commands: incorporate GenAI.mil platform familiarization into initial skills training pipelines NLT 01 October 2026.

5. EFFECTIVE DATE. This policy is effective 01 July 2026, unless superseded.

6. POC. Mr. Robert J. Hargrove (Notional), USD(A&S) Policy Office, robert.hargrove@osd.mil.notional, (703) 555-0280.

//SIGNED// (Notional)

NOTIONAL DOCUMENT — TRAINING USE ONLY
UNCLASSIFIED // FOR TRAINING USE ONLY

--- END NOTIONAL POLICY MEMO ---

Please rewrite in BLUF format, provide the key-dates table, and flag any ambiguities now.`;

// ── Poster prompts ────────────────────────────────────────────────────────────

const PICNIC_POSTER_PROMPT = `Create a vibrant, print-ready event poster (portrait orientation, 18×24 inches, 300 dpi) for a U.S. Army unit command picnic. Use the following details:

HEADLINE: "Anniston Army Depot Command & Family Picnic" (notional — training use only)
DATE/TIME: Saturday, 27 June · 11:00 AM – 4:00 PM
LOCATION: Lakeside Pavilion, Post Recreation Area, Anniston Army Depot (notional)
AUDIENCE: ~120 Soldiers, DA Civilians, and families welcome — kids encouraged

VISUAL ELEMENTS to include:
- A festive outdoor scene: shaded pavilion by a lake or park, picnic tables, American and Army flags
- Playful icons or illustrations: BBQ grill / food spread, lawn games (cornhole, tug-of-war, frisbee), kids' activities
- A bright summer sky, lush green grass

COLOR PALETTE: Army gold (#FFD700) and olive/Army green (#4B5320) as primary accents, warm sky blue background, white typography for headlines

LAYOUT:
- Large bold headline at top
- Event details in a clearly readable mid-section
- Illustrated scene filling the lower half
- A thin footer ribbon: "NOTIONAL · FOR TRAINING USE ONLY"

TONE: Friendly, inclusive, family-celebration energy. Suitable for posting on a command bulletin board.`;

const DEPERSONALIZE_POSTER_PROMPT = `Create a professional, print-ready awareness poster (portrait, 18×24 inches, 300 dpi) for a U.S. Navy Fleet Readiness Center on the topic: "Depersonalize Before You Use Commercial AI."

HEADLINE: "Protect What's Ours — Depersonalize First"
SUB-HEADLINE: "CUI and PII must never enter a commercial AI tool"

VISUAL CONCEPT:
- A clean, high-tech Navy aesthetic (dark navy blue background, white and gold accents)
- Center visual: a document/file icon being "sanitized" — names and ID numbers replaced by neutral tokens like PERSON_1, BUNO_1, HULL_1 before passing through a stylized digital gateway
- Left side: red-shaded zone labeled "Original Record — CUI/PII" with caution styling
- Right side: green-shaded zone labeled "Tokenized — Safe for Commercial AI" 

ICONS/CALLOUTS (small):
- ⚠ Do not paste CUI into ChatGPT, Claude, or Gemini
- ✓ Depersonalize on GenAI.mil (IL5) first
- ✓ Keep the token map inside the IL5 system

COLOR PALETTE: Navy dark blue (#003087), gold (#C5A028), white text, red/green accent zones

FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · Fleet Readiness Center Southwest"`;

const REPERSONALIZE_POSTER_PROMPT = `Create a clean, professional workflow infographic poster (portrait, 18×24 inches, 300 dpi) illustrating the 3-step AI drafting workflow for U.S. Navy maintenance personnel. Title: "The IL-Compliant AI Drafting Loop."

THREE STEPS — show as a horizontal or vertical flow with connecting arrows:

STEP 1 — DEPERSONALIZE (on GenAI.mil, IL5)
Icon: shield with checkmark + document
Label: "Strip PII & CUI — replace with tokens (PERSON_1, BUNO_1, etc.)"
Color accent: Navy blue

STEP 2 — DRAFT (any commercial AI — ChatGPT, Claude, Gemini)
Icon: chat bubble / AI sparkle
Label: "Paste tokenized document into any commercial AI. Get your polished draft."
Color accent: Teal / sky blue

STEP 3 — RE-PERSONALIZE (back on GenAI.mil, IL5)
Icon: lock + checkmark
Label: "Swap tokens back using your token map. Final IL5-ready memo."
Color accent: Green

BOTTOM CALLOUT BOX: "The token map NEVER leaves the IL5 environment."

VISUAL STYLE: Clean government/DoD infographic look, sans-serif typography, white background with colored lane accents
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · AIBMM Level 2 — Prompted Operator"`;

const NAVY_CARRIER_POSTER_PROMPT = `Create a commanding, professional poster (portrait, 18×24 inches, 300 dpi) for a U.S. Navy Regional Maintenance Center on the topic: "Fleet Readiness Starts with Knowing Your Backlog."

HEADLINE: "Know Your Availability — Own the Schedule"
SUB-HEADLINE: "AI-Assisted Carrier Maintenance Planning | Q3/Q4 FY26 (Notional)"

VISUAL CONCEPT:
- A dramatic aerial view of a carrier in drydock or at a shipyard pier, with cranes and maintenance activity visible
- Overlaid data callouts (stylized): ship silhouettes labeled CVN-74, CVN-76, CVN-78, CVN-73 with colored status bars (on-schedule = green, at-risk = amber, critical = red)
- A bold BLUF callout box: "Total portfolio: $2.69B · 2 availabilities at schedule risk · Welder shortage: 38 vacancies"

CALLOUT STRIPS (bottom third):
- "Propulsion shaft bearing: 8-week lead time" — RISK
- "Drydock slot conflict: waiver pending" — RISK
- "Weapons elevator software: cert NLT 15 Aug 2026" — WATCH
- "CVN-73 CNO availability: $1.8B sole-source" — DECISION REQUIRED

COLOR PALETTE: Navy dark blue, silver/grey, amber for risk items, red for critical, white typography
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · Regional Maintenance Center"`;

const AF_ALC_AOG_POSTER_PROMPT = `Create a striking, urgent-tone maintenance status poster (portrait, 18×24 inches, 300 dpi) for a U.S. Air Force Air Logistics Complex. Topic: "AOG Status — Know Your Priority, Clear the Flightline."

HEADLINE: "Aircraft On Ground — Act Now"
SUB-HEADLINE: "ALC Production Stand-Up · 9 Jun 2026 (Notional)"

VISUAL CONCEPT:
- Dark charcoal / Air Force blue background for a serious, high-stakes feel
- Top section: silhouettes of 5 grounded aircraft with colored status indicators
  - B-2A (stealth bomber): RED — Wing/CC authority required, COMSEC hold
  - F-22A: AMBER — Parts on hand, 24-hr repair
  - F-15E: AMBER — AWP, EDD 22 Jun
  - C-17A: AMBER — Cannibal approved, 48-hr labor
  - KC-135R: YELLOW — DLA expedite, EDD 27 Jun
- Center: bold priority matrix (1–5 ranking with aircraft MDS, unit, and key action)
- Bottom callout: "Return to FMC: 1 in 72 hrs · 3 within 7 days · 1 beyond 7 days"

COLOR PALETTE: Air Force blue (#003087 variant), silver, red/amber/yellow status colors, white bold text
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · Air Logistics Complex"`;

const ARMY_DEPOT_POSTER_PROMPT = `Create a bold, no-nonsense production-floor poster (portrait, 18×24 inches, 300 dpi) for a U.S. Army maintenance depot. Topic: "Prioritize the Queue — Every Hour Counts."

HEADLINE: "48 Hours. 69 Hours of Work. Make Every Wrench Turn Count."
SUB-HEADLINE: "Army Depot Work-Order Prioritization | Production Cycle Ending 20 Jun 2026 (Notional)"

VISUAL CONCEPT:
- Industrial / depot floor aesthetic: olive drab, Army gold, dark grey, heavy sans-serif typography
- A large priority stack or Kanban-style graphic showing 8 work orders as "cards" sorted top to bottom:
  1. WO-4403 · HMMWV · SOUR TAG · 4 hrs — SAFETY FIRST
  2. WO-4401 · M1A2 SEPv3 · DEPLOYING UNIT · 12 hrs
  3. WO-4407 · AN/TPQ-53 Radar · AIR DEFENSE URGENT · 6 hrs
  4. WO-4402 · M88A2 HERCULES · Recovery Asset · 8 hrs
  5. WO-4405 · M1070 HET · Critical Transport · 6 hrs
  — DEFER to next cycle: WO-4404, WO-4406, WO-4408 (greyed out)
- Bold callout: "21-hr shortfall — defer 2–3 orders. SOUR and deployment-readiness first."

COLOR PALETTE: Army olive (#4B5320), gold (#FFD700), dark grey background, white text, red highlight for SOUR/safety
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · Army Depot Production Control"`;

const BLUF_POSTER_PROMPT = `Create a sharp, instructional communication-skills poster (portrait, 18×24 inches, 300 dpi) for DoD depot and headquarters personnel. Topic: "Write It BLUF — Bottom Line Up Front."

HEADLINE: "Say It First. Say It Once. Make It BLUF."
SUB-HEADLINE: "The military standard for clear, actionable written communication"

VISUAL CONCEPT:
- Clean, modern look — white background, DoD blue and gold accents
- Left column: a "BEFORE" example — a dense, 6-paragraph policy memo opening with background context (visually overwhelming, red X overlay)
- Right column: an "AFTER" BLUF rewrite — 3 short, bold paragraphs labeled ①, ②, ③ (green checkmark overlay)

THREE RULES displayed prominently in a callout box:
  ① Paragraph 1: The single most important takeaway — one sentence.
  ② Paragraph 2: What must change, and by when.
  ③ Paragraph 3: Who is responsible and where to get help.

SECONDARY callout: "AI can turn a 600-word policy memo into a 3-paragraph BLUF in under 60 seconds — but you still own the judgment call."

COLOR PALETTE: White background, DoD blue (#003087), gold accent, red/green for before/after contrast
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · AIBMM Level 2 — Prompted Operator"`;

const AAR_OPSEC_POSTER_PROMPT = `Create a serious, mission-focused OPSEC awareness poster (portrait, 18×24 inches, 300 dpi) for a U.S. Air Force Reserve Wing. Theme: "Lessons Learned — OPSEC Never Sleeps."

HEADLINE: "One Post. One Picture. One Breach."
SUB-HEADLINE: "OPSEC Lessons from Operation NORTHERN HORIZON 26-04 (Notional After-Action Review)"

VISUAL CONCEPT:
- Stark, high-contrast design: dark Air Force blue / near-black background, sharp white and amber typography
- Top third: silhouette of a KC-46A Pegasus tanker against a night sky
- Center: three large warning callouts styled as incident cards:
  ① "Pre-deployment data transferred via personal Gmail — CUI spillage risk"
  ② "Geo-tagged social media posts revealed aircraft tail numbers and unit patches"
  ③ "Augmentee DMS accounts not provisioned — 47-min comms gap during divert"
- Bottom: a bold lesson banner: "AI can draft your AAR analysis — but only if you practice OPSEC first."

COLOR PALETTE: Air Force dark blue (#003087), near-black, amber (#FFB300) for warning cards, white bold text
FOOTER: "NOTIONAL · FOR TRAINING USE ONLY · 999th Air Refueling Wing (Notional) · AIBMM Level 3"`;

// ── Export ────────────────────────────────────────────────────────────────────

export const GENAI_MIL_SCENARIOS: GenAIScenario[] = [
  {
    id: 'aar-northern-horizon-26-04',
    title: 'AAR — Op NORTHERN HORIZON 26-04 (Training AAR Review)',
    blurb: 'Notional AFRC After-Action Report. Ask the AI to identify OPSEC issues, apply 8-Step Problem Solving framing to root-cause observations, and recommend corrective actions with a CCIR/SIR routing summary.',
    body: AAR_NORTHERN_HORIZON_PROMPT,
    level: 3,
    icon: 'ClipboardList',
    actionLabel: 'Analyze AAR',
    posterPrompt: AAR_OPSEC_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Understand the document',
        detail: 'Read the notional AAR overview in the prompt. Note the five key events: pre-deployment data leak via Gmail, inflight boom hydraulic failure, contractor conduct issue, OPSEC social-media violation, and an augmentee DMS provisioning gap.',
      },
      {
        title: 'Launch in GenAI.mil and send the prompt',
        detail: 'Tap "Launch in GenAI.mil" — the full AAR prompt will be pre-filled. Hit Send and watch the AI reason through each finding.',
      },
      {
        title: 'Review the BLUF and OPSEC findings',
        detail: 'The AI should produce a BLUF paragraph first, then list each OPSEC and conduct issue. Discuss with your group: did the AI catch all five events? Did it miss anything or hallucinate one that isn\'t there?',
      },
      {
        title: 'Evaluate the 8-Step / Fishbone framing',
        detail: 'The AI should apply structured root-cause framing to the five observations (para 5 of the AAR). Compare its framing against the "Initial Root Cause Observations" section. Is it accurate? Is it useful for a commander?',
      },
      {
        title: 'Check the corrective-action recommendations',
        detail: 'The AI should recommend corrective actions and a CCIR/SIR routing summary. Validate against the AAR\'s para 6. Key point: the AI is reasoning over a complex document — this is a Level 3 agent behavior, not a simple Level 2 prompt.',
      },
      {
        title: 'Debrief: what makes this Level 3?',
        detail: 'Contrast with Level 2: you pasted one document and got a structured multi-step analysis. At true Level 3, this would trigger automatically whenever a new AAR hits a shared drive — same output, zero manual effort, every time.',
      },
    ],
  },
  {
    id: 'depersonalize-navy-frc-record',
    title: 'Level 2: Depersonalize — Navy FRC Maintenance Record',
    blurb: 'Strip PII/CUI from a notional Navy Fleet Readiness Center record so it is safe to paste into a commercial AI tool. Demonstrates Step 1 of the IL-compliance workflow.',
    body: DEPERSONALIZE_NAVY_FRC_PROMPT,
    level: 2,
    icon: 'ShieldOff',
    actionLabel: 'Depersonalize record',
    posterPrompt: DEPERSONALIZE_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Understand the IL rule',
        detail: 'This record contains PII (names, EDIPI, email, phone) and CUI (BuNos, hull designator, unit designators, part serial numbers). It cannot be pasted into a commercial AI tool like ChatGPT or Claude.ai as-is — that would be a data spillage risk.',
      },
      {
        title: 'Send the prompt in GenAI.mil (IL5)',
        detail: 'Tap "Launch in GenAI.mil" — the depersonalize prompt is pre-filled with the full notional FRCSW maintenance record. Hit Send. GenAI.mil is IL5-authorized, so it\'s safe to run the original CUI document here.',
      },
      {
        title: 'Review the tokenized output',
        detail: 'The AI should return a "TOKENIZED document" with all PII/CUI replaced by PERSON_1, BUNO_1, HULL_1, etc. Verify: are all names, EDIPI numbers, BuNos, emails, phone numbers, hull designators, unit designators, and part serial numbers replaced? If the AI missed any, that\'s a teaching point.',
      },
      {
        title: 'Review the token map table',
        detail: 'The AI should also produce a two-column Token → Original Value table. Save this — you\'ll need it for the Re-personalize scenario. Discuss: why is this table itself CUI? (It maps tokens back to real identifiers — it must stay in the IL5 system.)',
      },
      {
        title: 'Copy the tokenized document',
        detail: 'The tokenized output is now safe to paste into any commercial AI tool. You would copy it and open Claude.ai, ChatGPT, or Gemini to draft your maintenance trend analysis memo.',
      },
      {
        title: 'Key takeaway',
        detail: 'Depersonalize on GenAI.mil → draft with any commercial AI → re-personalize on GenAI.mil. The token map never leaves the IL5 environment.',
      },
    ],
  },
  {
    id: 'repersonalize-ai-draft',
    title: 'Level 2: Re-personalize — Restore Tokens into AI Draft',
    blurb: 'Substitute placeholder tokens back into an AI-generated draft to produce the final IL5-ready memo. Completes the depersonalize → commercial AI → re-personalize loop.',
    body: REPERSONALIZE_DRAFT_PROMPT,
    level: 2,
    icon: 'ShieldCheck',
    actionLabel: 'Re-personalize draft',
    posterPrompt: REPERSONALIZE_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Set the context',
        detail: 'This scenario picks up after the Depersonalize step. You pasted the tokenized record into a commercial AI (Claude.ai, ChatGPT, or Gemini), which produced a professional maintenance memo using tokens like PERSON_1 and BUNO_1. Now you need to restore the real values.',
      },
      {
        title: 'Send the prompt in GenAI.mil (IL5)',
        detail: 'Tap "Launch in GenAI.mil" — the prompt contains the token map AND the tokenized AI-generated draft. Hit Send. This step runs in GenAI.mil (IL5) because the token map is CUI.',
      },
      {
        title: 'Review the re-personalized memo',
        detail: 'The AI should produce a fully substituted memo with all PERSON_*, BUNO_*, HULL_* tokens replaced by their real values. Scan it carefully: are all 25+ tokens correctly swapped? Did the AI flag any uncertain substitutions?',
      },
      {
        title: 'Discuss the workflow loop',
        detail: 'Draw the three-step flow on a whiteboard: (1) Depersonalize on GenAI.mil → (2) Draft on any commercial AI → (3) Re-personalize on GenAI.mil. The CUI never left the IL5 environment, but you got the full power of a commercial LLM for the drafting step.',
      },
      {
        title: 'Key takeaway',
        detail: 'This workflow is the highest-impact Level 2 skill for depot teams. It enables any commercial AI for drafting while maintaining IL5 compliance for any record containing PII, BuNos, EDIPI, or unit designators.',
      },
    ],
  },
  {
    id: 'command-picnic-planner',
    title: 'Level 2: Public Task — Command Picnic Planner',
    blurb: 'Non-sensitive, non-CUI task: plan a unit picnic. Any public tool is fine — no depersonalization needed. Shows when and why the IL compliance workflow is NOT required.',
    body: COMMAND_PICNIC_PROMPT,
    level: 2,
    icon: 'Sun',
    actionLabel: 'Plan the picnic',
    posterPrompt: PICNIC_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Identify the data sensitivity',
        detail: 'Before prompting any AI, always ask: does this task involve CUI, PII, or classified information? Unit picnic planning — headcount, food, games, park location — is entirely non-sensitive. No depersonalization needed.',
      },
      {
        title: 'Choose any AI tool',
        detail: 'For non-CUI tasks, Claude.ai, ChatGPT.com, or Gemini are completely appropriate. You can run this on GenAI.mil too, but you don\'t have to. The point of this scenario is to demonstrate that the IL workflow is not always required.',
      },
      {
        title: 'Send the prompt and review the invitation email',
        detail: 'Tap "Launch in GenAI.mil" to try it in the simulator. Review the draft all-hands invitation email — is it upbeat, under 200 words, and suitable for a command bulletin board?',
      },
      {
        title: 'Review the event checklist',
        detail: 'The AI should produce a checklist with the five required categories: Food & Drinks, Activities & Games, Logistics & Setup, Communications, and Safety & Medical. Discuss: is this a useful starting point for a unit event coordinator?',
      },
      {
        title: 'Discuss the teaching point',
        detail: 'The key lesson: AI tools are most useful when people know which tool to use for which task. Using GenAI.mil for everything creates unnecessary friction; using commercial tools for CUI creates risk. The correct answer depends on the data.',
      },
    ],
  },
  {
    id: 'navy-carrier-availability-bluf',
    title: 'Level 2: Navy — Carrier Availability Backlog BLUF',
    blurb: 'Notional carrier maintenance availability table → BLUF memo for the Admiral\'s brief. Demonstrates summarizing complex depot backlog data in under 2 minutes.',
    body: NAVY_CARRIER_AVAILABILITY_PROMPT,
    level: 2,
    icon: 'Ship',
    actionLabel: 'Draft the BLUF memo',
    posterPrompt: NAVY_CARRIER_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Review the availability table',
        detail: 'The prompt contains a notional Q3/Q4 FY26 carrier maintenance schedule with four hulls (CVN-73 through CVN-78), availability types (PIA, DSRA, ISRA, CNO), cost data ($89M–$1.8B), and specific risk flags. This is the kind of data a Deputy Commander would review for a Monday brief.',
      },
      {
        title: 'Send the prompt in GenAI.mil',
        detail: 'Tap "Launch in GenAI.mil." The prompt is pre-filled. Hit Send — the AI should produce a 2-page-max BLUF memo within seconds.',
      },
      {
        title: 'Evaluate the BLUF structure',
        detail: 'The memo should lead with total workload and cost exposure, identify the two highest-risk availabilities, note workforce/parts concerns, and include one recommended action per risk. Check: does it lead with the bottom line, or does it bury it?',
      },
      {
        title: 'Spot-check the risk prioritization',
        detail: 'CVN-74-NTL is 12 days behind with an 8-week part lead time; CVN-73-NTL has a $1.8B sole-source shipyard risk. Did the AI correctly identify these as the top two risks? Did it accurately represent the data?',
      },
      {
        title: 'Discuss time savings',
        detail: 'Ask the group: how long would it take to manually draft this BLUF from the same table? 20–45 minutes? The AI produced a draft in under a minute. The value isn\'t replacing the commander\'s judgment — it\'s eliminating the blank-page problem.',
      },
    ],
  },
  {
    id: 'af-alc-aog-triage',
    title: 'Level 2: Air Force — AOG Parts Triage Memo',
    blurb: 'Notional Air Logistics Complex AOG status list → prioritized triage memo for the Wing Commander. Demonstrates AI-assisted maintenance decision support.',
    body: AF_ALC_AOG_PROMPT,
    level: 2,
    icon: 'AlertTriangle',
    actionLabel: 'Draft the triage memo',
    posterPrompt: AF_ALC_AOG_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Review the AOG status table',
        detail: 'Five aircraft on-ground across four MDS (F-15E, C-17A, B-2A, F-22A, KC-135R). The B-2A has a COMSEC hold requiring Wing/CC authority; the F-22A has parts on-hand and a 24-hour repair window. Understanding the data before you prompt leads to better evaluation of the output.',
      },
      {
        title: 'Send the prompt in GenAI.mil',
        detail: 'Tap "Launch in GenAI.mil." The AI should produce a one-page BLUF triage memo with aircraft prioritized by mission impact (highest first).',
      },
      {
        title: 'Check the priority ordering',
        detail: 'Expected order: B-2A (stealth, Wing/CC authority, longest AOG), F-22A (combat-coded, quick win — parts on hand), F-15E (AWP, long EDD), C-17A (cannibal approved, 48 hrs), KC-135R (DLA expedite). Did the AI get this right? What reasoning did it use?',
      },
      {
        title: 'Check the authority flag',
        detail: 'The prompt requires flagging items that need MAJCOM or depot commander decision authority. The B-2A has a COMSEC hold — did the AI correctly flag it? This is a critical check: AI assistants must surface decision authority requirements clearly.',
      },
      {
        title: 'Review the bottom-line paragraph',
        detail: 'The AI should categorize aircraft by return-to-FMC timeline: within 72 hours, within 7 days, and beyond 7 days. Verify accuracy against the EDD column in the data table.',
      },
    ],
  },
  {
    id: 'army-depot-workorder-prioritization',
    title: 'Level 2: Army — Depot Work-Order Prioritization',
    blurb: 'Notional Army depot work-order queue with a 21-hr shortfall → prioritized schedule and commander summary. Demonstrates AI triage and production planning support.',
    body: ARMY_DEPOT_WORKORDER_PROMPT,
    level: 2,
    icon: 'ListOrdered',
    actionLabel: 'Prioritize work orders',
    posterPrompt: ARMY_DEPOT_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Understand the capacity problem',
        detail: 'Eight work orders totaling 69 labor-hours; only 48 hours available. The AI must apply Army maintenance priority criteria — mission criticality, SOUR flags, time-since-deadline, and cost efficiency — to decide what gets done and what gets deferred.',
      },
      {
        title: 'Review key constraints before prompting',
        detail: 'Three items jump out: NTL-WO-4401 (M1A2, deploying unit, URGENT), NTL-WO-4403 (HMMWV, SOUR tag — grounded for safety), NTL-WO-4407 (AN/TPQ-53 radar, air defense URGENT). Also note the master tech is on leave through Jun 15, affecting M1A2 work.',
      },
      {
        title: 'Send the prompt in GenAI.mil',
        detail: 'Tap "Launch in GenAI.mil." The AI should produce a prioritized list with clear reasoning, identify deferred work orders and why, then draft a one-paragraph production summary for the Depot Commander.',
      },
      {
        title: 'Evaluate the prioritization logic',
        detail: 'Expected top priority: WO-4403 (SOUR — safety first, 4 hrs), WO-4401 (M1A2 deployment, 12 hrs), WO-4407 (air defense URGENT, 6 hrs), WO-4402 (M88A2 recovery asset, 8 hrs), WO-4405 (HET transport, 6 hrs) = 36 hrs. Did the AI handle the master tech constraint for the M1A2?',
      },
      {
        title: 'Review the commander summary paragraph',
        detail: 'The paragraph should be concise, BLUF-style, and suitable to send to the Depot Commander without edits. Ask the group: would you send this as-is, or does it need modification? What would you change?',
      },
      {
        title: 'Debrief the time value',
        detail: 'In a real production control room, this analysis would take 15–30 minutes to do manually. The AI draft takes under a minute and gives a Production Control Officer a solid starting point for the commander\'s decision.',
      },
    ],
  },
  {
    id: 'bluf-rewrite-policy-memo',
    title: 'Level 2: BLUF Rewrite — AI Policy Memo (DoD Notional)',
    blurb: 'Dense notional DoD policy memo on AI tool usage at depots → BLUF summary, key-dates table, and ambiguity flags. Shows how to make policy actionable with AI.',
    body: BLUF_REWRITE_PROMPT,
    level: 2,
    icon: 'FileText',
    actionLabel: 'Rewrite as BLUF',
    posterPrompt: BLUF_POSTER_PROMPT,
    walkthrough: [
      {
        title: 'Skim the original policy memo',
        detail: 'The notional USD(A&S) memo is about 600 words, spans five numbered paragraphs, and contains six distinct action items with different due dates (July 1, July 15, August 1, September 30, October 1, 2026). This is a typical DoD policy document — dense, important, and hard to act on without analysis.',
      },
      {
        title: 'Send the prompt in GenAI.mil',
        detail: 'Tap "Launch in GenAI.mil." The AI should produce: (1) a 3-paragraph BLUF rewrite, (2) a key-dates / actions table, and (3) flagged ambiguities.',
      },
      {
        title: 'Evaluate the BLUF (3 paragraphs)',
        detail: 'Paragraph 1 should be one sentence: the core takeaway (CUI must go through IL5 systems effective 1 July 2026). Paragraph 2: what changes and by when. Paragraph 3: who is responsible and POC. Does the AI\'s rewrite pass the "30-second brief" test?',
      },
      {
        title: 'Verify the key-dates table',
        detail: 'Check the table for completeness against the memo. It should include: 1 Jul 2026 (policy effective), 15 Jul 2026 (Service instructions + AI Coordinator designated), 1 Aug 2026 (unit AI Use Policy), 30 Sep 2026 (JKO training), 1 Oct 2026 (Training Command pipeline update). Any missing dates?',
      },
      {
        title: 'Review the ambiguity flags',
        detail: 'Good AI responses should flag: What counts as a "DoD-approved enterprise agreement" for commercial tools? What\'s the exact process for the CUI spillage report? Is the JKO course # NTL-AI-101 really a real course? These flags are what make AI useful for staff officers — surfacing what needs commander clarification before publishing to units.',
      },
    ],
  },

  // ── Scenario: Depot Morning Huddle ──────────────────────────────────────────

  {
    id: 'depot-morning-huddle',
    level: 2,
    title: 'Level 2: Depot Morning Huddle — AI-Assisted Production Brief',
    blurb:
      'Download the three notional depot spreadsheets, choose the right AI tool for your data sensitivity, upload, and run a six-step prompt arc that turns raw WIP, supply, and throughput data into a 0700 stand-up brief.',
    icon: 'Sun',
    actionLabel: 'Run morning huddle prompts',
    dataFiles: [
      { filename: 'Depot_WIP_NOTIONAL.csv', label: 'Depot WIP', rows: '55 WOs' },
      { filename: 'Depot_Supply_AWP_NOTIONAL.csv', label: 'Supply / AWP Parts Hold', rows: '26 parts' },
      { filename: 'Depot_Schedule_Throughput_NOTIONAL.csv', label: 'Schedule & Throughput', rows: '12 mo × 5 lines' },
    ],
    body: `You are an expert depot production analyst. I am uploading three notional data files:
1. Depot_WIP_NOTIONAL.csv — 55 work orders currently on the floor (status, shop, platform, ECD, % complete, AWP/EXREP flags)
2. Depot_Supply_AWP_NOTIONAL.csv — 26 supply line items holding up AWP jobs (backorder status, lead time, source, EDD)
3. Depot_Schedule_Throughput_NOTIONAL.csv — 12 months of production data across 5 product lines (planned vs actual output, TAT, schedule adherence)

All data is NOTIONAL and for training purposes only.

Step 1 — Orientation: Summarize the WIP file. How many jobs, by status and by shop?`,
    walkthrough: [
      {
        title: 'Pick your tool based on data sensitivity',
        detail:
          'These files are NOTIONAL — no real CUI, PII, or operational data. That means any approved LLM (ChatGPT, Claude, Gemini) is fine for this exercise. If you were running this with real maintenance records, NALCOMIS exports, or BuNos: GenAI.mil (IL5) only, or depersonalize first.',
      },
      {
        title: 'Download the three CSV files',
        detail:
          'Click each file card below to download Depot_WIP_NOTIONAL.csv, Depot_Supply_AWP_NOTIONAL.csv, and Depot_Schedule_Throughput_NOTIONAL.csv. Keep them in a folder together — you will upload all three at once.',
      },
      {
        title: 'Upload all three files in a single session',
        detail:
          'Open GenAI.mil (for CUI/real data) or ChatGPT/Claude (for this notional exercise). Attach all three CSVs before sending the first message. The AI needs all three loaded to cross-reference across the datasets in later steps.',
      },
      {
        title: 'Run Step 1 — Orientation',
        detail:
          '"Summarize this WIP file. How many jobs, by status and by shop?" This proves the AI read the data correctly and gives you a baseline count before asking harder questions.',
      },
      {
        title: 'Run Steps 2–4 — Escalating complexity',
        detail:
          'Step 2: List every job past its ECD that is not yet RFI, sorted by days past ECD. Step 3: Cross-reference the AWP jobs against the supply file — which are stuck on a single backordered, sole-source OEM part? Step 4: Rank the EXREP jobs by likelihood of making ECD and identify which two need command attention.',
      },
      {
        title: 'Run Steps 5–6 — The brief writes itself',
        detail:
          'Step 5: "Draft a 5-bullet production status brief for the 0700 stand-up: throughput vs plan, top 3 schedule risks, AWP impact, and one recommended action." Step 6: "Based on actual vs target TAT trends by product line, which line is drifting and what is the likely impact on next quarter\'s output?" Point out to the room: that brief took 8 seconds and zero PowerPoint.',
      },
    ],
    posterPrompt:
      'Military-style poster, flat vector illustration, deep navy and slate background. Central graphic: a command-center table with three green spreadsheet icons fanning out toward an AI chat terminal on a monitor. Bold headline: "AI MORNING HUDDLE" in white stencil font. Subtitle: "Download. Upload. Brief in 8 seconds." Small text at bottom: "AIBMM Level 2 — Prompted Operator". Green accent stripe on left edge. Professional, minimal, no classified markings.',
  },
];
